import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

class WaslaChildPage {
  constructor(root) {
    this.root = root;
    const sharedPurchase = root.querySelector('[data-shared-purchase]');
    if (sharedPurchase) {
      root.dataset.addLabel = sharedPurchase.dataset.purchaseAddLabel;
      root.dataset.packOne = sharedPurchase.dataset.purchasePackOne;
      root.dataset.packTwo = sharedPurchase.dataset.purchasePackTwo;
      root.dataset.packMany = sharedPurchase.dataset.purchasePackMany;
    }
    this.abort = new AbortController();
    this.signal = this.abort.signal;
    this.form = root.querySelector('[data-child-form]');
    this.variant = root.querySelector('[data-child-variant]');
    this.image = root.querySelector('[data-child-image]');
    this.message = root.querySelector('[data-child-message]');
    this.cartLink = root.querySelector('[data-child-cart-link]');
    this.add = root.querySelector('[data-child-add]');
    this.addLabel = root.querySelector('[data-child-add-label]');
    this.sticky = root.querySelector('[data-child-sticky]');
    this.stickyAdd = root.querySelector('[data-child-sticky-add]');
    this.labels = JSON.parse(root.querySelector('[data-child-labels]')?.textContent || '{}');
    this.quantity = root.querySelector('[data-child-quantity]');
    this.minus = root.querySelector('[data-child-minus]');
    this.plus = root.querySelector('[data-child-plus]');
    this.galleryTrack = root.querySelector('[data-child-gallery-track]');
    this.gallerySlides = [...root.querySelectorAll('[data-child-slide]')];
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.money = new Intl.NumberFormat(document.documentElement.lang || 'ar-QA', { style: 'currency', currency: root.dataset.currency || 'QAR', maximumFractionDigits: 2, minimumFractionDigits: 0 });
    root.addEventListener('click', (event) => this.onClick(event), { signal: this.signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise?.then(({ cart }) => {
        if (cart?.totalQuantity === undefined) return;
        document.querySelectorAll('.wasla-header a[aria-label^="سلة التسوق"]').forEach((link) => {
          let badge = link.querySelector('.wh-cart-count');
          if (!badge && cart.totalQuantity > 0) {
            badge = document.createElement('span');
            badge.className = 'wh-cart-count';
            link.append(badge);
          }
          if (badge) { badge.textContent = cart.totalQuantity; badge.hidden = cart.totalQuantity === 0; }
          link.setAttribute('aria-label', `${this.labels.cartLabel}، ${cart.totalQuantity}`);
        });
      }).catch(() => {});
    }, { signal: this.signal });
    this.form?.addEventListener('change', (event) => {
      if (event.target === this.quantity || event.target === this.variant) this.normalizeQuantity();
      this.render();
    }, { signal: this.signal });
    this.quantity?.addEventListener('input', () => this.render(), { signal: this.signal });
    this.variant?.addEventListener('change', () => {
      const src = this.variant.selectedOptions[0]?.dataset.image;
      if (src) {
        this.selectPhoto(src);
        this.setImage(this.root.querySelector('[data-child-close-image]'), src, this.labels.selectedImage);
        this.root.querySelectorAll('[data-child-pack] img:not([data-child-pack-fixed])').forEach((image) => { image.src = src; image.removeAttribute('srcset'); });
        this.root.querySelectorAll('[data-child-photo]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.childPhoto === src)));
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.variant.value);
      history.replaceState({}, '', url);
    }, { signal: this.signal });
    this.form?.addEventListener('submit', (event) => this.onSubmit(event), { signal: this.signal });
    this.setupGallery();
    this.setupChapters();
    this.setupMotion();
    this.render();
    root.dataset.ready = 'true';
  }

  rules() {
    const option = this.variant.selectedOptions[0];
    return { option, min: Number(option?.dataset.min) || 1, step: Number(option?.dataset.step) || 1, max: Number(option?.dataset.max) || Infinity };
  }

  normalizeQuantity() {
    const { min, max, step } = this.rules();
    const value = this.quantity.valueAsNumber;
    const candidate = Number.isFinite(value) ? value : min;
    const allowedMax = Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Number.MAX_SAFE_INTEGER;
    this.quantity.value = Math.min(allowedMax, Math.max(min, min + Math.round((candidate - min) / step) * step));
  }

  render() {
    const { option, min, step, max } = this.rules();
    if (!option) return;
    const available = option.dataset.available === 'true';
    const price = Number(option.dataset.price);
    const oldPrice = Number(option.dataset.compare || 0);
    const unitPrice = this.root.querySelector('[data-child-unit-price]');
    if (unitPrice) unitPrice.textContent = this.money.format(price / 100);
    const unitCompare = this.root.querySelector('[data-child-unit-compare]');
    if (unitCompare) { unitCompare.hidden = oldPrice <= price; unitCompare.textContent = this.money.format(oldPrice / 100); }
    const saving = this.root.querySelector('[data-child-saving]');
    if (saving) {
      saving.hidden = oldPrice <= price;
      saving.querySelector('[data-child-saving-value]').textContent = this.money.format((oldPrice - price) / 100);
    }
    this.quantity.min = min;
    this.quantity.step = step;
    if (Number.isFinite(max)) this.quantity.max = max; else this.quantity.removeAttribute('max');
    const quantity = this.quantity.valueAsNumber;
    const valid = Number.isSafeInteger(quantity) && quantity >= min && quantity <= max && (quantity - min) % step === 0;
    this.quantity.setAttribute('aria-invalid', String(!valid));
    this.quantity.disabled = Boolean(this.busy) || !available;
    this.variant.disabled = Boolean(this.busy);
    this.root.querySelectorAll('[data-child-variant-choice]').forEach((choice) => {
      const variantOption = [...this.variant.options].find((item) => item.value === choice.dataset.childVariantChoice);
      choice.disabled = Boolean(this.busy) || variantOption?.dataset.available !== 'true';
      choice.setAttribute('aria-pressed', String(choice.dataset.childVariantChoice === this.variant.value));
    });
    this.minus.disabled = Boolean(this.busy) || !available || !valid || quantity <= min;
    this.plus.disabled = Boolean(this.busy) || !available || !valid || quantity >= max;
    this.root.querySelectorAll('[data-child-pack]').forEach((pack) => {
      const packQuantity = min + Number(pack.dataset.packIndex) * step;
      pack.dataset.packQuantity = packQuantity;
      pack.disabled = !available || packQuantity > max || Boolean(this.busy);
      pack.setAttribute('aria-pressed', String(valid && quantity === packQuantity));
      pack.querySelector('[data-child-pack-name]').textContent = packQuantity === 1 ? this.root.dataset.packOne : packQuantity === 2 ? this.root.dataset.packTwo : `${packQuantity} ${this.root.dataset.packMany}`;
      pack.querySelector('[data-child-pack-price]').textContent = this.money.format(price * packQuantity / 100);
    });
    const total = valid ? this.money.format(price * quantity / 100) : '—';
    this.root.querySelector('[data-child-total]').textContent = total;
    this.root.querySelector('[data-child-sticky-total]').textContent = total;
    const compare = this.root.querySelector('[data-child-compare]');
    compare.hidden = !valid || oldPrice <= price;
    compare.textContent = valid ? this.money.format(oldPrice * quantity / 100) : '';
    this.add.disabled = !available || Boolean(this.busy) || !valid;
    this.stickyAdd.disabled = this.add.disabled;
    this.addLabel.textContent = this.busy ? this.labels.adding : available ? this.root.dataset.addLabel || this.labels.add : this.labels.soldOut;
    this.form.setAttribute('aria-busy', String(Boolean(this.busy)));
  }

  onClick(event) {
    const choice = event.target.closest('[data-child-variant-choice]');
    if (choice && !choice.disabled && !this.busy) {
      this.variant.value = choice.dataset.childVariantChoice;
      this.variant.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const pack = event.target.closest('[data-child-pack]');
    if (pack && !pack.disabled && !this.busy) {
      this.quantity.value = pack.dataset.packQuantity;
      this.render();
    }
    const adjust = event.target.closest('[data-child-minus],[data-child-plus]');
    if (adjust && !adjust.disabled && !this.busy) {
      const { step } = this.rules();
      this.quantity.value = this.quantity.valueAsNumber + (adjust.hasAttribute('data-child-plus') ? step : -step);
      this.normalizeQuantity();
      this.render();
    }
    const button = event.target.closest('[data-child-photo]');
    if (button) {
      this.selectPhoto(button.dataset.childPhoto);
    }
    const direction = event.target.closest('[data-child-gallery-direction]');
    if (direction && !direction.disabled) this.goToPhoto(this.galleryIndex + Number(direction.dataset.childGalleryDirection));
    const anchor = event.target.closest('a[href^="#child-"],a[data-ws-purchase-link]');
    if (anchor) {
      const target = document.getElementById(anchor.hash.slice(1));
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: this.reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
        const heading = target.querySelector('h1,h2');
        if (heading) { heading.tabIndex = -1; heading.focus({ preventScroll: true }); }
        else if (target.matches('form')) { target.tabIndex = -1; target.focus({ preventScroll: true }); }
      }
    }
  }

  setImage(image, src, alt) {
    if (!image || !src) return;
    image.classList.add('is-changing');
    image.removeAttribute('srcset');
    image.src = src;
    image.alt = alt || '';
    const settle = () => image.classList.remove('is-changing');
    image.addEventListener('load', settle, { once: true, signal: this.signal });
    image.addEventListener('error', settle, { once: true, signal: this.signal });
    if (image.complete) settle();
  }

  setupGallery() {
    if (!this.galleryTrack || !this.gallerySlides.length) return;
    this.galleryIndex = 0;
    this.updateGallery();
    this.galleryTrack.addEventListener('scroll', () => {
      if (this.galleryFrame) return;
      this.galleryFrame = requestAnimationFrame(() => {
        this.galleryFrame = null;
        this.galleryIndex = Math.max(0, Math.min(this.gallerySlides.length - 1, Math.round(this.galleryTrack.scrollLeft / this.galleryTrack.clientWidth)));
        this.updateGallery();
      });
    }, { passive: true, signal: this.signal });
    this.galleryTrack.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.gallerySlides.length - 1 : this.galleryIndex + (event.key === 'ArrowRight' ? 1 : -1);
      this.goToPhoto(index);
    }, { signal: this.signal });
  }

  selectPhoto(src) {
    const index = this.gallerySlides.findIndex((slide) => slide.dataset.src === src);
    if (index >= 0) this.goToPhoto(index);
    else this.setImage(this.image, src, this.labels.selectedImage);
  }

  goToPhoto(index) {
    if (!this.galleryTrack) return;
    this.galleryIndex = Math.max(0, Math.min(this.gallerySlides.length - 1, index));
    this.galleryTrack.scrollTo({left:this.galleryIndex * this.galleryTrack.clientWidth, behavior:this.reducedMotion.matches ? 'instant' : 'smooth'});
    this.updateGallery();
  }

  updateGallery() {
    const current = this.gallerySlides[this.galleryIndex];
    const count = this.root.querySelector('[data-child-gallery-count]');
    if (count) count.textContent = this.galleryIndex + 1;
    this.root.querySelectorAll('[data-child-photo]').forEach((photo) => photo.setAttribute('aria-pressed', String(photo.dataset.childPhoto === current?.dataset.src)));
    this.root.querySelectorAll('[data-child-gallery-direction]').forEach((button) => { button.disabled = Number(button.dataset.childGalleryDirection) < 0 ? this.galleryIndex === 0 : this.galleryIndex === this.gallerySlides.length - 1; });
  }

  setupChapters() {
    const chapters = [...this.root.querySelectorAll('[data-child-chapter]')];
    chapters.forEach((chapter) => chapter.addEventListener('toggle', () => {
      if (!chapter.open) return;
      chapters.forEach((other) => { if (other !== chapter) other.open = false; });
      this.setImage(this.root.querySelector('[data-child-journey-image]'), chapter.dataset.image, chapter.dataset.alt);
    }, { signal: this.signal }));
  }

  setupMotion() {
    if (!('IntersectionObserver' in window)) return;
    this.revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (!this.reducedMotion.matches) entry.target.classList.add('wc-revealing');
        this.revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    this.root.querySelectorAll('[data-child-reveal]').forEach((element) => this.revealObserver.observe(element));
    const earlySticky = this.root.hasAttribute('data-child-sticky-early');
    const mobilePurchase = window.matchMedia('(max-width: 899px)');
    let buyEntry;
    const updateSticky = () => {
      if (!buyEntry) return;
      const beforeOffer = !earlySticky || !mobilePurchase.matches;
      this.sticky.hidden = buyEntry.isIntersecting || (beforeOffer && buyEntry.boundingClientRect.bottom >= 0);
    };
    this.buyObserver = new IntersectionObserver(([entry]) => { buyEntry = entry; updateSticky(); }, { threshold: 0 });
    this.buyObserver.observe(this.root.querySelector(earlySticky ? '.wc-order' : '.wc-hero'));
    mobilePurchase.addEventListener('change', updateSticky, { signal: this.signal });
    const purchaseStops = new Set();
    this.footerObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) purchaseStops.add(entry.target); else purchaseStops.delete(entry.target); });
      this.sticky.style.visibility = purchaseStops.size ? 'hidden' : '';
    });
    const footer = document.querySelector('.footer-section, #footer-group, footer');
    if (footer) this.footerObserver.observe(footer);
    const close = this.root.querySelector('.wc-close,[data-ws-campaign-final]');
    if (close) this.footerObserver.observe(close);
    const picture = this.root.querySelector('[data-child-drift]');
    const scene = picture?.closest('.wc-memory');
    if (scene && picture) {
      this.driftObserver = new IntersectionObserver(([entry]) => { this.driftVisible = entry.isIntersecting; });
      this.driftObserver.observe(scene);
      let queued = false;
      const onScroll = () => {
        if (queued || !this.driftVisible || this.reducedMotion.matches) return;
        queued = true;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          queued = false;
          const box = scene.getBoundingClientRect();
          const progress = Math.max(0, Math.min(1, (innerHeight - box.top) / (innerHeight + box.height)));
          picture.style.transform = `translateY(${(progress - .5) * 36}px) scale(1.07)`;
        });
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal: this.signal });
      this.root.closest('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true, signal: this.signal });
      this.reducedMotion.addEventListener('change', () => { if (this.reducedMotion.matches) picture.style.transform = ''; }, { signal: this.signal });
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.busy || this.add.disabled) return;
    const id = this.variant.value;
    const quantity = this.quantity.valueAsNumber;
    if (!this.form.checkValidity()) { this.form.reportValidity(); return; }
    if (!id || !Number.isSafeInteger(quantity) || quantity < 1) return;
    this.busy = true;
    this.message.hidden = true;
    this.message.classList.remove('is-error');
    this.cartLink.hidden = true;
    this.render();
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});
    this.root.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: id, quantity }], promise: deferred.promise }));
    let added = false;
    try {
      const route = window.Shopify?.routes?.root || '/';
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((component) => component.dataset.sectionId))].slice(0, 5);
      const response = await fetch(`${route}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', signal: this.signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }], sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(response.status === 422 ? this.labels.stockError : this.labels.error);
      added = true;
      const cartResponse = await fetch(`${route}cart.js`, { cache: 'no-store', credentials: 'same-origin', signal: this.signal, headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.labels.addedReview);
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
        items: cart.items, source: 'wasla-child', sourceId: this.form.id, itemCount: quantity,
        productId: this.root.dataset.productId, sections: result.sections, didError: false,
      } });
      this.message.textContent = this.labels.added;
      this.message.hidden = false;
      this.cartLink.hidden = false;
      requestAnimationFrame(() => document.getElementById('cart-drawer')?.open?.());
    } catch (error) {
      // Shopify can add the remaining inventory while returning 422. Reconcile
      // the real cart even on failure so counts and drawer contents stay true.
      let hasSelectedItem = false;
      try {
        const route = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${route}cart.js`, { cache: 'no-store', credentials: 'same-origin', signal: this.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw error;
        const cart = await response.json();
        hasSelectedItem = cart.items.some((item) => String(item.variant_id) === id);
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
          items: cart.items, source: 'wasla-child', sourceId: this.form.id,
          productId: this.root.dataset.productId, didError: !added,
        } });
      } catch { deferred.reject(error); }
      if (!added) this.root.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
      this.message.textContent = added ? this.labels.addedReview : error.message || this.labels.error;
      this.message.classList.toggle('is-error', !added);
      this.message.hidden = false;
      this.cartLink.hidden = !added && !hasSelectedItem;
    } finally { this.busy = false; this.render(); }
  }

  disconnect() {
    this.abort.abort();
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.galleryFrame) cancelAnimationFrame(this.galleryFrame);
    [this.revealObserver, this.buyObserver, this.footerObserver, this.driftObserver].forEach((observer) => observer?.disconnect());
  }
}
const pages = new Map();
function mount(scope = document) {
  for (const [root, instance] of pages) if (!root.isConnected) { instance.disconnect(); pages.delete(root); }
  scope.querySelectorAll('[data-child-page]').forEach((root) => { if (!pages.has(root)) pages.set(root, new WaslaChildPage(root)); });
}
mount();
document.addEventListener('shopify:section:load', () => mount());
document.addEventListener('shopify:section:unload', (event) => {
  for (const [root, instance] of pages) if (event.target.contains(root)) { instance.disconnect(); pages.delete(root); }
});
